import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaCoffee, FaSpinner } from 'react-icons/fa';
import axios from '../config/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [warmingServer, setWarmingServer] = useState(false);
  const [serverWarm, setServerWarm] = useState(false);
  const warmupPromiseRef = useRef<Promise<boolean> | null>(null);
  const { login, loginAsGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as { from?: { pathname?: string; search?: string } } | null)
    ?.from;
  const from = fromState?.pathname || '/cafes';
  const fromSearch = fromState?.search && fromState.search.startsWith('?') ? fromState.search : '';

  const warmServerIfNeeded = async (): Promise<boolean> => {
    if (serverWarm) return true;
    if (warmupPromiseRef.current) return warmupPromiseRef.current;

    setWarmingServer(true);
    warmupPromiseRef.current = (async () => {
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          const res = await axios.get('/test-server', {
            timeout: 20000,
            __skipWakeRetry: true,
          } as any);
          if (res?.data?.status === 'ok' || res?.data?.status === 'starting') {
            setServerWarm(true);
            return true;
          }
        } catch {
          // best-effort wake-up; keep retrying below
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      return false;
    })();

    try {
      return await warmupPromiseRef.current;
    } finally {
      warmupPromiseRef.current = null;
      setWarmingServer(false);
    }
  };

  useEffect(() => {
    void warmServerIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      const safe =
        from && from.startsWith('/') && !from.startsWith('//') && from !== '/login'
          ? from
          : '/cafes';
      navigate(`${safe}${fromSearch}`);
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || 
                          err.message || 
                          (err.code === 'ECONNABORTED' ? 'Request timeout - please check your connection' : 
                          err.code === 'ERR_NETWORK' ? 'Network error - please check if the server is running' :
                          'Login failed. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setError('');
    setGuestLoading(true);
    try {
      const ready = await warmServerIfNeeded();
      if (!ready) {
        throw new Error('Server is waking up. Please retry in a few seconds.');
      }
      await loginAsGuest();
      navigate('/cafes');
    } catch (err: any) {
      console.error('Guest login error:', err);
      const errorMessage =
        err.response?.data?.error ||
        (err.code === 'ECONNABORTED'
          ? 'Guest sign-in timed out. Please try again in a few seconds.'
          : err.code === 'ERR_NETWORK'
            ? 'Network error - cannot reach server right now.'
            : err.message || 'Guest login failed. Please try again.');
      setError(errorMessage);
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md border border-amber-100">
        <div className="text-center mb-6">
          <FaCoffee className="text-5xl text-amber-700 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mt-2 text-amber-800">Welcome to QahwaHub</h2>
          <p className="text-gray-600 mt-1">Your daily brew, just a tap away</p>
        </div>
        
        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
            <p className="font-semibold">Error:</p>
            <p>{error}</p>
            {error.includes('Network error') && (
              <p className="text-xs mt-2">
                💡 Check your connection. For a dev build, set REACT_APP_API_URL and rebuild the app.
              </p>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>
          <div className="mb-2">
            <label className="block text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>
          <p className="mb-6 text-right text-sm">
            <Link to="/forgot-password" className="text-amber-700 hover:underline font-semibold">
              Forgot password?
            </Link>
          </p>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-800 hover:to-amber-700 text-white py-3 rounded-lg transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-[1.02] disabled:hover:scale-100 flex items-center justify-center gap-2"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)', pointerEvents: loading ? 'none' : 'auto' }}
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Logging in...</span>
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>
        
        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-gray-500 text-sm">or</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>
        
        <button
          type="button"
          onClick={handleGuestLogin}
          disabled={guestLoading || loading}
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)' }}
          className="w-full bg-gradient-to-r from-gray-100 to-gray-50 hover:from-gray-200 hover:to-gray-100 text-gray-700 py-3 rounded-lg transition-all duration-200 font-semibold border border-gray-300 shadow-sm hover:shadow-md transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {guestLoading ? (
            <>
              <FaSpinner className="animate-spin" />
              <span>Starting…</span>
            </>
          ) : (
            'Continue as Guest'
          )}
        </button>
        <p className="text-xs text-gray-500 text-center mt-2">
          Browse cafes and menus without an account
        </p>
        {warmingServer && !guestLoading && (
          <p className="text-xs text-amber-700 text-center mt-2">
            Waking up server for faster sign-in…
          </p>
        )}
        
        <p className="mt-6 text-center text-gray-600">
          Don't have an account?{' '}
          <Link to="/register" className="text-amber-700 hover:underline font-semibold">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}
